<template>
  <v-app>
    <v-main class="main">

      <!-- Sidebar -->
      <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

        <!-- Table Card -->
        <v-card class="table-card mt-6">

          <div class="table-header d-flex justify-space-between align-center flex-wrap ga-3">
            <!-- Left side: search + clear -->
            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
              <InputText v-model="filters['global'].value" placeholder="Search products..." />
              <Button type="button" icon="pi pi-filter-slash" label="Clear" variant="outlined" @click="clearFilters" />
            </div>
            <button type="button" :disabled="loading" class="theme-button px-5"
              @click="resetForm(); isEditing = false; editingId = null; productTypeDialog = true">
              <span v-if="!loading"><v-icon start>mdi-content-save-cog-outline</v-icon> Save Product Type </span>
            </button>
          </div>

          <!-- PrimeVue DataTable with search ---NB: add showGridlines to show column lines-->
          <DataTable v-model:filters="filters" :value="productType" paginator :rows="10" dataKey="uuid" stripedRows
            class="modern-table" :rowsPerPageOptions="[5, 10, 20, 50]" tableStyle="min-width: 50rem"
            filterDisplay="menu" selectionMode="single" :globalFilterFields="['name', 'pricing_model']">

            <!-- Empty table -->
            <template #empty>
              No product type found.
            </template>

            <!-- Loading state -->
            <template #loading>
              Loading products...
            </template>

            <!-- Product Column -->
            <Column header="Product Type">
              <template #body="slotProps">
                <div class="product-cell">
                  <img
                    :src="slotProps.data.product_type_image ? `${STORAGE_URL}/${slotProps.data.product_type_image}` : defaultImage"
                    class="product-image" />
                  <div>
                    <div class="product-name">{{ slotProps.data.name }}</div>
                    <div class="product-desc">{{ truncate(slotProps.data.description) }}</div>
                  </div>
                </div>
              </template>
            </Column>

            <!-- Min tenure Column -->
            <Column header="Pricing Model">
              <template #body="slotProps">
                {{ slotProps.data.pricing_model }}
              </template>
            </Column>

            <!-- Available Volume Column-->
            <Column header="Fields Required">
              <template #body="slotProps">
                <div class="d-flex flex-wrap ga-2">
                  <span v-for="(field, i) in parseFields(slotProps.data.fields_required)" :key="i" class="status-pill">
                    {{ field }}
                  </span>
                </div>
              </template>
            </Column>

            <!-- Purchased Volume Column-->
            <Column header="Created on">
              <template #body="slotProps">
                {{ formatDateTime(slotProps.data.created_at) }}
              </template>
            </Column>


            <!-- Actions Column -->
            <Column header="" style="width:120px; border-radius: 10px;">
              <template #body="slotProps">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn icon v-bind="props" variant="text">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="openEditModal(slotProps.data)">
                      Edit {{ slotProps.data.name }} Details
                    </v-list-item>
                  </v-list>

                </v-menu>
              </template>
            </Column>

          </DataTable>
        </v-card>
        <v-dialog v-model="productTypeDialog" max-width="650">
          <v-card class="pa-6">

            <v-card-title class="text-h6 mb-4 text-center">
              {{ isEditing ? 'Edit Product Type' : 'Create Product Type' }}
            </v-card-title>

            <form @submit.prevent="handleCreateType" class="space-y-5">


              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Name</label>
                <v-text-field v-model="form.name" type="text" variant="outlined" placeholder="Product Type Name" />
              </div>

              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium">Description</label>
                <v-textarea v-model="form.description" variant="outlined" rows="2" />
              </div>

              <!-- Pricing Model -->
              <div class="flex flex-col gap-2">
                <!-- <label class="text-sm font-medium">Pricing Model</label> -->
                <v-select v-model="form.pricing_model" :items="priceModel" variant="outlined" item-title="label"
                  item-value="value" :rules="[v => !!v || 'Pricing Model is required']" label="Select Pricing Model" />
              </div>

              <!-- Risk Category -->
              <div class="flex flex-col gap-2">
                <!-- <label class="text-sm font-medium">Risk Category</label> -->
                <v-select v-model="form.risk_category_id" :items="riskCat" variant="outlined" item-title="name"
                  item-value="uuid" :rules="[v => !!v || 'Interest type is required']" label="Select Risk Category" />
              </div>

              <!-- Interest -->
              <div class="flex flex-col gap-2">
                <!-- <label class="text-sm font-medium">Interest Type</label> -->
                <v-select v-model="form.interest_type_id" :items="interestType" variant="outlined" item-title="name"
                  item-value="uuid" :rules="[v => !!v || 'Pricing Model is required']" label="Select Interest Type" />
              </div>
              <div class="flex flex-col gap-2">
                <v-select v-model="form.fields_required" :items="requiredFields" item-title="name" item-value="name"
                  label="Required Fields" multiple chips variant="outlined"
                  :rules="[v => !!v || 'Field Required is important']" />
              </div>
              <div class="flex flex-col gap-2 mt-4">
                <!-- <label class="text-sm font-medium mb-4">Type Image</label> -->

                <v-file-input v-model="form.product_type_image" variant="outlined" accept="image/*" label="Upload image"
                  prepend-icon="mdi-camera" />
                <img v-if="existingImage" :src="`${STORAGE_URL}/${existingImage}`" class="mt-3 rounded" width="120" />
              </div>

              <!-- ACTIONS -->
              <div class="flex justify-center align-center  gap-3 pt-4"
                style="display:flex; justify-content:center; align-items:center; gap:12px; padding-top:16px;">
                <v-btn variant="text" @click="productTypeDialog = false">
                  Cancel
                </v-btn>
                <v-btn color="black" type="submit" :loading="typeLoading" :disabled="!isFormValid">
                  {{ isEditing ? 'Update Product Type' : 'Save Product Type' }}
                </v-btn>
              </div>

            </form>
          </v-card>
        </v-dialog>
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top right"> {{
          snackbar.message }}
          <template #actions>
            <v-btn variant="outlined" @click="snackbar.show = false">
              Close
            </v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue"
import AdminSidebar from "@/components/AdminSidebar.vue"
import Header from "@/components/Header.vue"
import { useProductStore } from "@/store/product"
import { useInvStore } from "@/store/investment"
import { STORAGE_URL } from "@/api/env"
import avatarImage from '@/assets/crowdital-icon.png'
import { formatDateTime, getAllErrors } from "@/util"
import { addProductType, updateProductType } from "@/api/product"

//const auth = useAuthStore()
const prd = useProductStore()
const invt = useInvStore()

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const filters = ref({ global: { value: null, matchMode: "contains" } })
const defaultImage = avatarImage
const typeLoading = ref(false)
const isEditing = ref(false)
const editingId = ref(null)
const productTypeDialog = ref(false)
const existingImage = ref("")

/* Mobile */
const toggleSidebar = () => {
  if (isMobile.value) drawerOpen.value = !drawerOpen.value
  else isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  drawerOpen.value = !isMobile.value
}

/* Products */
const productType = computed(() => prd.product_type || [])

const interestType = computed(() => invt.interestType || [])
const riskCat = computed(() => invt.riskCategory || [])


/* Lifecycle */
onMounted(async () => {
  checkMobile()
  window.addEventListener("resize", checkMobile)
  
  try {
    await prd.loadProductTypes()
    await invt.loadInterestType()
    await invt.loadRiskCategory()
  } catch (error) {
    console.error(error)
  }
})

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

/* Utils */
const truncate = (text) => !text ? "" : text.length > 60 ? text.substring(0, 60) + "..." : text

/* Clear filters */
const clearFilters = () => {
  filters.value = { global: { value: null, matchMode: "contains" } }
}

const parseFields = (fields) => {
  try {
    // already array
    if (Array.isArray(fields)) {
      return fields
    }

    // stringified json
    if (typeof fields === "string") {
      const parsed = JSON.parse(fields)

      // double encoded
      if (typeof parsed === "string") {
        return JSON.parse(parsed)
      }

      return parsed
    }

    return []
  } catch (e) {
    console.error("Failed to parse fields:", e)
    return []
  }
}

//snackbar
const snackbar = ref({
  show: false,
  message: "",
  color: "success" // success | error | warning | info
})

const showSnackbar = (message, color = "success") => {
  snackbar.value.message = message
  snackbar.value.color = color
  snackbar.value.show = true
}

const priceModel = [
  { label: "Discount", value: "Discount" },
  { label: "Fixed Interest", value: "Fixed Interest" },
  { label: "Nav", value: "Nav" },
  { label: "Profit Share", value: "Profit Share" },
  { label: "Not Applicable", value: "Not Applicable" }
]

const form = ref({
  name: "",
  description: "",
  pricing_model: "",
  risk_category_id: "",
  interest_type_id: "",
  product_type_image: null,
  fields_required: []
})

/**
 * FORM VALIDATION STATE
 */
const isFormValid = computed(() => {
  return (
    form.value.name &&
    form.value.description &&
    form.value.pricing_model &&
    form.value.risk_category_id &&
    form.value.interest_type_id &&
    form.value.fields_required &&
    form.value.product_type_image
  )
})

/**
 * RESET FORM
 */
const resetForm = () => {
  form.value = {
    name: "",
    description: "",
    pricing_model: "",
    risk_category_id: "",
    interest_type_id: "",
    product_type_image: null,
    fields_required: [],
  },
  existingImage.value = ""
}

//required field
const requiredFields = ref([
  { id: 1, name: "Minimum Tenure" },
  { id: 2, name: "Minimum Amount" },
  { id: 3, name: "Buy Price" },
  { id: 4, name: "Sell Price" },
  { id: 5, name: "Maturity Date" },
  { id: 6, name: "Available Volume" },
  { id: 7, name: "Interest Rate" }
])


//Function to create or edit
const handleCreateType = async () => {
  typeLoading.value = true

  try {
    const payload = new FormData()

    payload.append("name", form.value.name)
    payload.append("description", form.value.description)
    payload.append("pricing_model", form.value.pricing_model)
    payload.append("risk_category_id", form.value.risk_category_id)
    payload.append("interest_type_id", form.value.interest_type_id)
    // payload.append("fields_required", form.value.fields_required)
    form.value.fields_required.forEach((item, index) => {
      payload.append(`fields_required[${index}]`, item)
    })
    payload.append("product_type_image", form.value.product_type_image)

    //await executeFeeConfig(payload)
    if (isEditing.value) {
      // update api
      await updateProductType(editingId.value, payload)
    } else {
      // create api
      await addProductType(payload)
    }

    // reload updated data
    await prd.loadProductTypes()

    //close the modal
    showSnackbar("Product type saved successfully")

    //resetForm()

  } catch (e) {


    const msg = getAllErrors(e)
    showSnackbar(
      msg || "Failed to save product type",
      "error"
    )
  } finally {
    typeLoading.value = false
  }
}

//editing modal
const openEditModal = (item) => {
  isEditing.value = true
  editingId.value = item.uuid

  form.value.name = item.name || ""
  form.value.description = item.description || "",
  form.value.pricing_model = item.pricing_model || "",
  form.value.risk_category_id = item.risk_category?.uuid || item.risk_category_id || ""
  form.value.interest_type_id = item.interest_type?.uuid || item.interest_type_id || ""
  form.value.product_type_image = item.product_type_image,
  form.value.fields_required[item.fields_required]

  form.value.product_type_image = null

  existingImage.value = item.product_type_image || ""

  // parse fields_required safely
  form.value.fields_required = parseFields(item.fields_required)

  productTypeDialog.value = true
}

</script>

<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background: #f8fafc;
}

/* Card */
.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

/* Header */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* Table */
.modern-table {
  border: none;
}

/* Product */
.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
}

.product-name {
  font-weight: 700;
}

.product-desc {
  font-size: 16px;
  color: #64748b;
}

/* Status */
.status-pill {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: #ecfdf5;
  color: #16a34a;
}

.status-inactive {
  background: #fef2f2;
  color: #dc2626;
}

/* Search input */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #64748b;
}

.search-wrapper input {
  padding-left: 28px;
}

/* Button */
.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.25s ease;
}

:deep(.modern-table .p-datatable-thead > tr > th) {
  background: #eef7f0;
  color: #065f46;
  font-weight: 600;
  border-bottom: 2px solid #c1edc3;
}

.p-datatable-tbody>tr {
  cursor: pointer;
}

.p-datatable-tbody>tr:hover {
  background-color: #f5f5f5;
}
</style>
